import _extends from "@babel/runtime/helpers/esm/extends";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
var _excluded = ["expanded", "defaultExpanded", "onChange"];
import PropTypes from 'prop-types';
import React, { forwardRef, useCallback, useState } from 'react';
import { AccordionHeader } from '../AccordionHeader/AccordionHeader';
import { AccordionItem } from '../AccordionItem/AccordionItem';
import { AccordionPanel } from '../AccordionPanel/AccordionPanel';
import { ControlledAccordion } from './ControlledAccordion';

/**
 * @subcomponents ['Accordion.Item', 'Accordion.Header', 'Accordion.Panel']
 * @since 8.43.0
 */

export var Accordion = /*#__PURE__*/forwardRef(function (_ref, forwardedRef) {
  var controlledExpanded = _ref.expanded,
    _ref$defaultExpanded = _ref.defaultExpanded,
    defaultExpanded = _ref$defaultExpanded === void 0 ? [] : _ref$defaultExpanded,
    onChange = _ref.onChange,
    rest = _objectWithoutProperties(_ref, _excluded);
  var _useState = useState(defaultExpanded),
    _useState2 = _slicedToArray(_useState, 2),
    uncontrolledExpanded = _useState2[0],
    setUncontrolledExpanded = _useState2[1];
  var onChangeCallback = useCallback(function (newValue, nextState) {
    setUncontrolledExpanded(nextState);
    if (typeof onChange === 'function') {
      onChange(newValue, nextState);
    }
  }, [onChange, setUncontrolledExpanded]);
  return /*#__PURE__*/React.createElement(ControlledAccordion, _extends({
    ref: forwardedRef,
    expanded: typeof controlledExpanded !== 'undefined' ? controlledExpanded : uncontrolledExpanded,
    onChange: onChangeCallback
  }, rest));
});
Accordion.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * The styled-components
   * [polymorphic "as"](https://styled-components.com/docs/api#as-polymorphic-prop) prop.
   */
  // eslint-disable-next-line zillow/react/forbid-prop-types
  as: PropTypes.any,
  children: PropTypes.node.isRequired,
  /**
   * A default value for the expanded panel's index, or indices, in an uncontrolled accordion
   * component when it is initially rendered.
   */
  defaultExpanded: PropTypes.oneOfType([PropTypes.number, PropTypes.arrayOf(PropTypes.number)]),
  /**
   * The index, or array of indices, for expanded accordion panels. The `expanded` prop should be
   * used along with `onChange` prop to create controlled accordion component.
   *
   * @ignoreInControls
   */
  expanded: PropTypes.oneOfType([PropTypes.number, PropTypes.arrayOf(PropTypes.number)]),
  /**
   * The callback for when accordion state has changed.
   *
   * This function will be passed the selected panel's `index` and the `nextState` as index or
   * array of indices.
   *
   * @ignoreInControls
   */
  onChange: PropTypes.func,
  /**
   * The callback for when accordion focus has changed.
   *
   * This function will be passed the focused panel's `index`.
   *
   * @ignoreInControls
   */
  onPanelFocus: PropTypes.func
} : {};
Accordion.displayName = 'Accordion';
Accordion.Item = AccordionItem;
Accordion.Header = AccordionHeader;
Accordion.Panel = AccordionPanel;